import { supabase } from "@/integrations/supabase/client";

export type PromoPlanKind = "bump" | "vip" | "premium";

export type PromoRequest = {
  id: string;
  listing_id: string;
  user_id: string;
  plan: PromoPlanKind;
  days: number;
  status: "pending" | "approved" | "rejected";
  note: string | null;
  created_at: string;
  reviewed_at: string | null;
};

export const promoPlanLabel: Record<PromoPlanKind, string> = {
  bump: "🚀 İrəli çək",
  vip: "👑 VIP",
  premium: "💎 Premium",
};

const db = supabase as unknown as {
  from: (t: string) => any;
};

/** Elan sahibi admin üçün müraciət yaradır. */
export async function createPromoRequest(input: {
  listingId: string;
  userId: string;
  plan: PromoPlanKind;
  days: number;
}) {
  const { data: existing } = await db
    .from("promo_requests")
    .select("id")
    .eq("listing_id", input.listingId)
    .eq("plan", input.plan)
    .eq("status", "pending")
    .maybeSingle();
  if (existing) return "exists" as const;

  const { error } = await db.from("promo_requests").insert({
    listing_id: input.listingId,
    user_id: input.userId,
    plan: input.plan,
    days: input.days,
  });
  if (error) throw error;
  return "created" as const;
}

export type AdminPromoRequest = PromoRequest & {
  listing_title: string;
  listing_category: string;
  listing_image: string | null;
  owner_name: string | null;
};

export async function fetchPromoRequests(): Promise<AdminPromoRequest[]> {
  const { data, error } = await db
    .from("promo_requests")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(300);
  if (error) throw error;
  const rows = (data ?? []) as PromoRequest[];
  if (rows.length === 0) return [];

  const listingIds = [...new Set(rows.map((r) => r.listing_id))];
  const userIds = [...new Set(rows.map((r) => r.user_id))];
  const [{ data: listings }, { data: profiles }] = await Promise.all([
    supabase.from("listings").select("id, title, category, images").in("id", listingIds),
    supabase.from("profiles").select("id, full_name").in("id", userIds),
  ]);
  const byListing = new Map((listings ?? []).map((l) => [l.id, l]));
  const byUser = new Map((profiles ?? []).map((p) => [p.id, p]));

  return rows.map((r) => {
    const l = byListing.get(r.listing_id);
    return {
      ...r,
      listing_title: l?.title ?? "Silinmiş elan",
      listing_category: l?.category ?? "",
      listing_image: l?.images?.[0] ?? null,
      owner_name: byUser.get(r.user_id)?.full_name ?? null,
    };
  });
}

/** Admin müraciəti təsdiqləyir: paket verilir və ya elan yuxarı çəkilir. */
export async function approvePromoRequest(req: PromoRequest) {
  if (req.plan === "bump") {
    const { error } = await supabase
      .from("listings")
      .update({ created_at: new Date().toISOString() })
      .eq("id", req.listing_id);
    if (error) throw error;
  } else {
    const expires = new Date(Date.now() + req.days * 24 * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
      .from("listings")
      .update({ badge: req.plan, badge_expires_at: expires, badge_price: 0 })
      .eq("id", req.listing_id);
    if (error) throw error;
  }
  const { error } = await db
    .from("promo_requests")
    .update({ status: "approved", reviewed_at: new Date().toISOString() })
    .eq("id", req.id);
  if (error) throw error;
}

export async function rejectPromoRequest(id: string, note?: string) {
  const { error } = await db
    .from("promo_requests")
    .update({ status: "rejected", reviewed_at: new Date().toISOString(), note: note ?? null })
    .eq("id", id);
  if (error) throw error;
}
